import React from "react";
import { User } from "lucide-react";
import type { SalaoProducaoLabels } from "../pages/Painel/utils/origemPainel";

export type SalaoProducaoOrderHeaderProps = {
  labels: SalaoProducaoLabels | null;
  /** Cardápio / fallback quando não é salão FoodFly. */
  fallbackBadge?: string;
  customerName?: string | null;
  variant?: "compact" | "prominent";
  /**
   * `inline` — Pedido · Mesa · Comanda na mesma linha.
   * `stacked` — Pedido na 1ª linha; Mesa + Comanda menores na 2ª.
   */
  layout?: "inline" | "stacked";
  showSalaoTag?: boolean;
  className?: string;
};

const chipBase =
  "inline-flex h-7 w-fit items-center justify-center rounded-full border px-2.5 py-1 text-xs font-extrabold uppercase leading-none tracking-wide";

const chipPedido = `${chipBase} border-slate-400 bg-slate-800 text-white`;
const chipMesa = `${chipBase} border-amber-300 bg-amber-100 text-amber-900`;
const chipComanda = `${chipBase} border-violet-300 bg-violet-50 text-violet-800`;
/** Chip verde — contraste no card dark red do salão. */
const chipSalao = `${chipBase} border-emerald-400 bg-emerald-600 text-white`;

const chipSecondaryBase =
  "inline-flex h-5 w-fit items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide";
const chipMesaStacked = `${chipSecondaryBase} border-amber-300 bg-amber-100 text-amber-900`;
const chipComandaStacked = `${chipSecondaryBase} border-violet-300 bg-violet-50 text-violet-800`;

const prominentBase =
  "inline-flex h-7 w-fit items-center justify-center rounded-xl border-2 px-2.5 py-1 shadow-md font-mono text-xs font-extrabold uppercase tabular-nums leading-none tracking-wide";
const prominentPedido = `${prominentBase} border-neutral-800 bg-neutral-900 text-white`;
const prominentMesa = `${prominentBase} border-amber-500 bg-amber-100 text-amber-950`;
const prominentComanda = `${prominentBase} border-violet-600 bg-violet-700 text-white`;

const lineClass =
  "flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-neutral-600 [[data-salao-card]_&]:text-white/90 [[data-paozinho-card]_&]:text-white/90";

/** Cabeçalho: Mesa · Comanda · Cliente (sem Pedido # quando há mesa/comanda). */
export const SalaoProducaoOrderHeader: React.FC<
  SalaoProducaoOrderHeaderProps
> = ({
  labels,
  fallbackBadge,
  customerName,
  variant = "compact",
  layout = "inline",
  showSalaoTag = false,
  className = "",
}) => {
  const cliente = labels?.cliente ?? customerName ?? null;
  const isProminent = variant === "prominent";
  const isStacked = layout === "stacked" && !isProminent;

  if (!labels) {
    return (
      <div className={`flex min-w-0 flex-col gap-1.5 ${className}`.trim()}>
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {fallbackBadge ? (
            isProminent ? (
              <div className={prominentPedido}>{fallbackBadge}</div>
            ) : (
              <span className={chipPedido}>{fallbackBadge}</span>
            )
          ) : null}
        </div>
        {cliente ? (
          <p className={lineClass}>
            <User size={14} />
            {cliente}
          </p>
        ) : null}
      </div>
    );
  }

  // Com mesa/comanda não mostrar Pedido # — só mesa, comanda e cliente.
  const showPedido =
    !!labels.pedidoLabel && !labels.mesa && !labels.comandaLabel;
  const showMesa = !!labels.mesa;
  const showComanda = !!labels.comandaLabel;
  const showSecondary =
    !labels.mesa &&
    !!labels.secondary &&
    labels.secondary !== labels.pedidoLabel;
  const showMesaComandaRow = isStacked && (showMesa || showComanda);

  const mesaChip = showMesa ? (
    isProminent ? (
      <div className={prominentMesa}>{labels.mesa}</div>
    ) : (
      <span className={isStacked ? chipMesaStacked : chipMesa}>
        {labels.mesa}
      </span>
    )
  ) : null;

  const comandaChip =
    showComanda && labels.comandaLabel ? (
      isProminent ? (
        <div className={prominentComanda}>{labels.comandaLabel}</div>
      ) : (
        <span className={isStacked ? chipComandaStacked : chipComanda}>
          {labels.comandaLabel}
        </span>
      )
    ) : null;

  return (
    <div className={`flex min-w-0 flex-col gap-1.5 ${className}`.trim()}>
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">
        {showSalaoTag ? <span className={chipSalao}>Salão</span> : null}

        {showPedido && labels.pedidoLabel ? (
          isProminent ? (
            <div className={prominentPedido}>{labels.pedidoLabel}</div>
          ) : (
            <span className={chipPedido}>{labels.pedidoLabel}</span>
          )
        ) : !showMesa && !showComanda && labels.badge ? (
          isProminent ? (
            <div className={prominentPedido}>{labels.badge}</div>
          ) : (
            <span className={chipPedido}>{labels.badge}</span>
          )
        ) : null}

        {!isStacked ? (
          <>
            {mesaChip}
            {comandaChip}
          </>
        ) : null}
      </div>

      {showMesaComandaRow ? (
        <div className="flex min-w-0 flex-wrap items-center gap-1">
          {mesaChip}
          {comandaChip}
        </div>
      ) : null}

      {showSecondary ? (
        <p className={lineClass}>{labels.secondary}</p>
      ) : null}

      {cliente ? (
        <p className={lineClass}>
          <User size={14} />
          {cliente}
        </p>
      ) : null}
    </div>
  );
};

export default SalaoProducaoOrderHeader;
